import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Background, Hr, Titulo, Button, Input, Row } from "./style";
import api from "../../../services/api";
import { Swal } from "../../../services/SwalTemplates";

const DECLARACAO_ACUMULO_STORAGE_KEY = "declaracaoAcumuloPayload";

function normalizeCollection(value) {
  if (!value) return [];
  if (!Array.isArray(value)) return [value];

  return value.flatMap((item) => (Array.isArray(item) ? item : [item]));
}

function normalizeTurmaColaborador(value) {
  if (!Array.isArray(value)) return normalizeCollection(value);

  const firstItem = value[0];
  if (Array.isArray(firstItem)) {
    return normalizeCollection(firstItem);
  }

  return normalizeCollection(value);
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
}

export default function Acumulo() {
  const [professores, setProfessores] = useState([]);
  const [professor, setProfessor] = useState("");
  const [matricula, setMatricula] = useState(999999);
  const [consulta, setConsulta] = useState([]);
  const navigate = useNavigate();
  const [projetoLancamento, setProjetoLancamento] = useState([]);
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    api
      .get(`/rh/units`)
      .then((response) => {
        setUnidades(Array.isArray(response?.data?.unidades) ? response.data.unidades : []);
      })
      .catch((error) => {
        Swal.fire({
          title: "Atenção!",
          text: "Não foi possível consultar os dados!",
          icon: "error",
        });
      });
  }, []);

  useEffect(() => {
    api
      .get(`/rh/contributors?inativo=${false}`)
      .then((response) => {
        setProfessores(response.data.colaboradores);
      })
      .catch((error) => {
        Swal.fire({
          title: "Atenção!",
          text: "Não foi possível consultar os dados!",
          icon: "error",
        });
      });
  }, []);

  useEffect(() => {
    api
      .get(`/rh/projetoslancamentos?matricula=${matricula}`)
      .then((response) => {
        const normalized = normalizeCollection(response?.data?.lancamentos);
        setProjetoLancamento(normalized);
      })
      .catch((error) => {
        Swal.fire({
          title: "Atenção!",
          text: "Não foi possível consultar os dados!",
          icon: "error",
        });
      });
  }, [matricula]);

  useEffect(() => {
    api
      .get(`/rh/turmacolaborador?matricula=${matricula}&consulta=${true}`)
      .then((response) => {
        setConsulta(normalizeTurmaColaborador(response?.data?.turma_colaborador));
      })
      .catch((error) => {
        Swal.fire({
          title: "Atenção!",
          text: "Não foi possível consultar os dados!",
          icon: "error",
        });
      });
  }, [matricula]);

  function handleDeclaracaoAcumulo() {
    if (!matricula || Number(matricula) === 999999 || !professor) {
      return Swal.fire("Atenção!", "Selecione o professor para gerar a declaração.", "warning");
    }

    const professorSelecionado = professores.find((p) => String(p.matricula) === String(matricula));
    const unidadesPorId = new Map(unidades.map((unidade) => [String(unidade.id), unidade]));
    const unidadesPorNome = new Map(unidades.map((unidade) => [normalizeText(unidade.nome), unidade]));

    const enrichWithUnidade = (registro) => {
      const unidadeEncontrada =
        unidadesPorId.get(String(registro?.id_unidade || "")) ||
        unidadesPorNome.get(normalizeText(registro?.unidade || registro?.nome_unidade));

      const cidadeUnidade = unidadeEncontrada?.cidade
        ? [unidadeEncontrada.cidade, unidadeEncontrada.estado].filter(Boolean).join("/")
        : registro?.cidade_unidade || registro?.cidade || "Indaiatuba/SP";

      return {
        ...registro,
        unidade: unidadeEncontrada?.nome || registro?.unidade || registro?.nome_unidade || "Sem unidade informada",
        nome_unidade: unidadeEncontrada?.nome || registro?.nome_unidade || registro?.unidade || "Sem unidade informada",
        endereco_unidade: unidadeEncontrada?.endereco || registro?.endereco_unidade || registro?.endereco || "Não informado",
        cidade_unidade: cidadeUnidade,
        cep_unidade: unidadeEncontrada?.cep || registro?.cep_unidade || registro?.cep || "Não informado",
        telefone_unidade: unidadeEncontrada?.telefone || registro?.telefone_unidade || registro?.telefone || "Não informado",
      };
    };

    const aulasEnriquecidas = normalizeCollection(consulta).map(enrichWithUnidade);
    const projetosEnriquecidos = normalizeCollection(projetoLancamento).map(enrichWithUnidade);

    const payload = {
      professor: professorSelecionado?.nome || professor,
      matricula,
      cpf: professorSelecionado?.cpf || null,
      regimeJuridico: professorSelecionado?.vinculo || null,
      professorDados: professorSelecionado || null,
      aulas: aulasEnriquecidas,
      projetos: projetosEnriquecidos,
      unidades,
    };

    localStorage.setItem(DECLARACAO_ACUMULO_STORAGE_KEY, JSON.stringify(payload));
    navigate("/pdfdeclaracaoacumulo");
  }

  return (
    <Background>
      <Container>
        <Titulo>Acúmulo</Titulo>
        <Hr />
        <Row>
          <Input>
            <div style={{ display: "flex" }}>
              <select
                value={professor}
                onChange={(e) => {
                  setProfessor(e.target?.value?.toUpperCase());
                  setMatricula(e.target[e.target.options.selectedIndex]?.id);
                }}
              >
                <option disabled selected id={999999} value={""}>
                  Selecione o professor
                </option>
                {professores.map((p, i) => (
                  <option key={i} id={p.matricula} title={p.matricula}>
                    {p.nome}
                  </option>
                ))}
              </select>
              <Button
                onClick={handleDeclaracaoAcumulo}
                style={{
                  cursor: "pointer",
                  opacity: 1,
                  pointerEvents: "auto",
                  width: "190px",
                }}
              >
                Gerar declaração
              </Button>
            </div>
          </Input>
        </Row>
      </Container>
    </Background>
  );
}
